import styles from './style.postcss';
import React from 'react';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import is from 'is_js';
import { Grid, connect, PrivilegeChecker } from 'omni-common-ui';
import Privileges from 'domain/Privileges';
import UnsavedChangesTrackingPageComponent from 'components/UnsavedChangesTrackingPageComponent';
import UnsavedChangesWarning from 'components/UnsavedChangesWarning';
import NoContentMessage from 'components/NoContentMessage';
import { actions as groupActions } from 'containers/Group';
import IndividualCard from './IndividualCard';
import ExportDialog from './ExportDialog';
import { EDIT, PUBLISH, EXPORT } from './routes';
import { List } from 'immutable';
import {
  accessors as writingAssignmentOverview,
  spinActions,
  coreActions,
  activityActions,
  commentsActions,
  essayActions,
  shortAnswerActions,
} from 'data/WritingAssignment';

const ESSAY = 'essay';
const SHORT_ANSWER = 'shortanswer';

class WritingAssignment extends UnsavedChangesTrackingPageComponent {

  constructor(props) {
    super(props);
    this.state = {
      nextLocation: '',
      comment: '',
      shortAnswers: new List(),
    };
    this._onEdit = this._onEdit.bind(this);
    this._onDiscardComment = this._onDiscardComment.bind(this);
    this._onSaveComment = this._onSaveComment.bind(this);
    this._onUnSaveClose = this._onUnSaveClose.bind(this);
    this._onDiscardUnsavedComment = this._onDiscardUnsavedComment.bind(this);
    this._onCommentUpdate = this._onCommentUpdate.bind(this);
    this._onShortAnswersUpdate = this._onShortAnswersUpdate.bind(this);
    this._onSaveUnsavedComment = this._onSaveUnsavedComment.bind(this);
  }

  componentDidMount() {
    this._fetchActivity(this.props);
  }

  componentWillReceiveProps(props) {
    this._fetchActivity(props);
  }

  routerWillLeave(nextLocation) {
    const pathname = nextLocation.pathname;
    const {
      activityId,
      publishWritingAssignmment,
      studentId,
     } = this.props;
    if (WritingAssignment.isInEditMode(this.props) &&
      this._isEssayChanged(pathname) &&
      this.state.nextLocation.length === 0) {
      this.setState({
        nextLocation: pathname,
      });
      return false;
    } else if (pathname.endsWith(PUBLISH)) {
      const payload = {
        studentId,
      };
      publishWritingAssignmment(activityId, payload).then(() => this._refreshData(this.props));
      return false;
    }
    return super.routerWillLeave(nextLocation);
  }

  _refreshData(props) {
    const {
      activityId,
      studentId,
      groupId,
      activityType,
      fetchEssay,
      fetchShortAnswer,
      fetchCoreWrittingAssignment,
      fetchSPINWrittingAssignment,
    } = props;
    const type = is.string(activityType) ? activityType.toLowerCase() : '';
    if (type === ESSAY) {
      return fetchEssay(activityId, studentId).then(() => {
        fetchCoreWrittingAssignment(groupId);
        fetchSPINWrittingAssignment(groupId);
      });
    }
    if (type === SHORT_ANSWER) {
      return fetchShortAnswer(activityId, studentId).then(() => {
        fetchCoreWrittingAssignment(groupId);
        fetchSPINWrittingAssignment(groupId);
      });
    }
  }

  _isEssayChanged(pathname) {
    return ! (pathname === this.props.buildRoute({ mode: '' }) ||
      pathname === this.props.buildRoute({ mode: EDIT }));
  }

  _fetchActivity(props) {
    const {
      activityId,
      activityType,
      studentId,
      writingAssignment,
      fetchEssayIfNeeded,
      fetchShortAnswerIfNeed,
    } = props;
    const type = is.string(activityType) ? activityType.toLowerCase() : '';
    if (type === ESSAY) {
      fetchEssayIfNeeded(activityId, studentId);
    }
    if (type === SHORT_ANSWER) {
      fetchShortAnswerIfNeed(activityId, studentId);
    }
    if (is.existy(writingAssignment)) {
      this.setState({
        comment: is.existy(writingAssignment.comment) ? writingAssignment.comment : '',
        shortAnswers: writingAssignment.shortAnswers || new List(),
      });
    }
  }

  _onUnSaveClose() {
    this.setState({ nextLocation: '' });
  }

  _onDiscardUnsavedComment() {
    this.props.push(this.state.nextLocation);
    this.setState({ nextLocation: '' });
  }

  _onSaveUnsavedComment() {
    this._submitComment(this.props, this.state.nextLocation);
    this.setState({ nextLocation: '' });
  }

  _onCommentUpdate(comment) {
    this.setState({ comment });
  }

  _onShortAnswersUpdate(shortAnswers) {
    this.setState({ shortAnswers });
  }

  _onEdit() {
    this.props.push(this.props.buildRoute({ mode: EDIT }));
  }

  _onSaveComment() {
    this._submitComment(this.props, this.props.buildRoute({ mode: '' }));
  }

  _submitComment(props, nextLocation) {
    const {
      activityId,
      studentId,
      submitWritingAssignmmentComment,
      submitShortAnswer,
      push,
      activityType,
    } = props;
    if (is.string(activityType) && activityType.toLowerCase() === ESSAY) {
      const payload = {
        studentId: parseInt(studentId, 10),
        comment: this.state.comment && this.state.comment.trim(),
      };
      submitWritingAssignmmentComment(activityId, payload).then(() => this._refreshData(props));
    }

    if (is.string(activityType) && activityType.toLowerCase() === SHORT_ANSWER) {
      const { shortAnswers } = this.state;
      const studentAnswerResult = shortAnswers.filter((shortAnswer) =>
        is.boolean(shortAnswer.isCorrect))
        .map((shortAnswer) => ({
          questionNo: parseInt(shortAnswer.questionNo, 10),
          isCorrect: shortAnswer.isCorrect,
        })).toJS();
      const payload = {
        studentId: parseInt(studentId, 10),
        // comment: this.state.comment && this.state.comment.trim(),
        studentAnswerResult,
      };
      submitShortAnswer(activityId, payload).then(() => this._refreshData(props));
    }
    push(nextLocation);
  }

  _onDiscardComment() {
    this.props.push(this.props.buildRoute({ mode: '' }));
  }

  _renderExportDialog() {
    const {
      activityId,
      groupId,
      studentId,
      mode,
      buildRoute,
      push,
      languageCode,
      exportName,
    } = this.props;
    const isInExportRoute = mode === EXPORT;
    return isInExportRoute &&
      <ExportDialog activityId={activityId}
          groupId={groupId}
          studentId={studentId}
          languageCode={languageCode}
          pushRoute={push}
          buildRoute={buildRoute}
          exportName={exportName} />;
  }

  render() {
    const { activityType, writingAssignment, timeZone, canEdit } = this.props;
    if (! is.existy(writingAssignment)) {
      return <NoContentMessage text="No Writing Assignment is submitted." />;
    }
    return <div className={styles.TextEditor_content}>
      <Grid>
        <Grid.Group>
          <IndividualCard writing={writingAssignment}
              isEdit={WritingAssignment.isInEditMode(this.props)}
              onEdit={this._onEdit}
              comment={this.state.comment}
              shortAnswers={this.state.shortAnswers}
              type={activityType}
              onCommentUpdate={this._onCommentUpdate}
              onShortAnswersUpdate={this._onShortAnswersUpdate}
              onDiscardComment={this._onDiscardComment}
              onSaveComment={this._onSaveComment}
              timeZone={timeZone}
              canEdit={canEdit} />
        </Grid.Group>
      </Grid>
      <UnsavedChangesWarning isOpen={this.state.nextLocation.length !== 0}
          onCloseClicked={this._onUnSaveClose}
          onSaveClicked={this._onSaveUnsavedComment}
          onDiscardClicked={this._onDiscardUnsavedComment} />
      {this._renderExportDialog()}
    </div>;
  }
}

WritingAssignment.isInEditMode = (props) => props.params && props.params.mode === EDIT;

WritingAssignment.propTypes = {
  activityId: PropTypes.string,
  activityType: PropTypes.string,
  mode: PropTypes.string,
  writingAssignment: PropTypes.object,
  studentId: PropTypes.string,
  groupId: PropTypes.string,
  languageCode: PropTypes.string,
  exportName: PropTypes.string,
  buildRoute: PropTypes.func,
  push: PropTypes.func,
  fetchEssayIfNeeded: PropTypes.func,
  fetchEssay: PropTypes.func,
  fetchShortAnswerIfNeed: PropTypes.func,
  fetchShortAnswer: PropTypes.func,
  submitWritingAssignmmentComment: PropTypes.func,
  publishWritingAssignmment: PropTypes.func,
};

function mapStateToProps(state, ownProps) {
  const { mode, lessonSeq, activityId, studentId, groupId } = ownProps.params;
  const { timeZone } = groupActions.getCentre(state, groupId);
  const activity = writingAssignmentOverview
    .getStudentActivityByLessonCategory(state, groupId, { lessonSeq, activityId }, studentId) || {};
  const writingAssignment = writingAssignmentOverview
    .getWritingAssignmentByActivityType(state,
        activity.activityType,
        activity.activityId,
        studentId);
  const group = state.get('rootReducer').get('groups').get(groupId).data;
  const languageCode = group.get('languageCode');
  const activityType = activity.activityType;
  const exportName = `WritingAssignment - ${activityType}.pdf`;
  return {
    activityId: activity.activityId,
    activityType,
    exportName,
    mode,
    writingAssignment,
    groupId,
    languageCode,
    studentId,
    timeZone,
    canEdit: PrivilegeChecker.hasPrivilege(state, Privileges.AssignHomework.ManageOwn),
  };
}

function mapDispatchToProps(dispatch) {
  return Object.assign({},
    bindActionCreators(activityActions, dispatch),
    bindActionCreators(commentsActions, dispatch),
    bindActionCreators(essayActions, dispatch),
    bindActionCreators(shortAnswerActions, dispatch),
    bindActionCreators(spinActions, dispatch),
    bindActionCreators(coreActions, dispatch),
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(WritingAssignment);



// WEBPACK FOOTER //
// ./containers/Group/WritingAssignment/StudentWritingAssignment/component.jsx